<script setup lang="ts">
    import { onMounted, inject, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatDate } from '@cwd/Shared/utils/formatDateTime';
    import { SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@shared/components/Form/Form';
    import {
        Chart as ChartJS,
        BarController,
        BarElement,
        CategoryScale,
        LinearScale,
        Title,
        Tooltip,
        Legend,
        ChartOptions,
        TooltipItem,
        ChartData,
    } from 'chart.js';
    import { Chart } from 'vue-chartjs';
    import { applyChartDefaults } from '@shared/modules/chartjs-defaults';
    import ChangeSubscriptionModal from './ChangeSubscriptionModal.vue';
    import CancellationReasonsModal from './CancellationReasonsModal.vue';
    import { subscriptionStatusEditOptions } from '@views/BackOffice/Subscription/SubscriptionStatus';
    import { SubscriptionAddonsAccess } from '@cwd/BackOffice/SubscriptionAddons/SubscriptionAddons.access';
    import { chartColor } from '@shared/modules/chart-colors';

    enum ServiceSubscriptionStatus {
        FreeTrial = 1,
        FreeTrialExpired = 2,
        Active = 3,
        Suspended = 4,
        Cancelled = 5,
        PaymentFailed = 6,
        Fraud = 7,
    }

    ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, BarController);
    applyChartDefaults();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);
    const subscriptionAddonsAccess: SubscriptionAddonsAccess = injectorAccess.injector.get(SubscriptionAddonsAccess);

    const props = defineProps<{
        serviceSubscriptionId: string;
    }>();

    const state = reactive<{
        subscriptionForm: FormProps;
        subscriptionFormReady: boolean;
        showChangeSubscriptionModal: boolean;
        chartData: ChartData<'bar'> | null;
        chartOptions: ChartOptions<'bar'> | null;
        showCancellationReasons: boolean;
    }>({
        subscriptionForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        subscriptionFormReady: false,
        showChangeSubscriptionModal: false,
        chartData: null,
        chartOptions: null,
        showCancellationReasons: false,
    });

    onMounted(async () => {
        setupSubscriptionFormData();

        await subscriptionAccess.getUsageData(props.serviceSubscriptionId);

        state.chartData = {
            labels: Object.keys(subscriptionAccess.state.usageData!.DailyUsage).map((item) => {
                var dateObj = new Date(item);
                return dateObj.getDate();
            }),
            datasets: [
                {
                    label: $Resources.AccountChartIssueCount,
                    data: Object.values(subscriptionAccess.state.usageData!.DailyUsage),
                    backgroundColor: chartColor('primary'),
                },
            ],
        };

        state.chartOptions = {
            animation: {
                duration: 0,
            },
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                tooltip: {
                    filter: function (tooltipItem: TooltipItem<'bar'>, tooltipIndex, tooltipItems, data) {
                        var value = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.dataIndex];
                        if (value === 0) {
                            return false;
                        } else {
                            return true;
                        }
                    },
                    intersect: false,
                    callbacks: {
                        title: (tooltipItems: TooltipItem<'bar'>[]) => {
                            if (tooltipItems.length === 0) return;

                            var tooltipItem = tooltipItems[0];

                            var label = Object.keys(subscriptionAccess.state.usageData!.DailyUsage)[tooltipItem.dataIndex];
                            return label ? formatDate(label) : '';
                        },
                    },
                },
                legend: {
                    display: false,
                },
            },
            scales: {
                y: {
                    ticks: {
                        display: false,
                    },
                },
                x: {
                    grid: {
                        display: false,
                    },
                },
            },
        };
    });

    function setupSubscriptionFormData(): void {
        state.subscriptionForm.data = {
            Start: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.Start,
                group: 'Subscription',
                required: false,
                input: {
                    label: 'Start',
                    type: 'date',
                },
            },
            End: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.End,
                group: 'Subscription',
                required: false,
                input: {
                    label: 'End',
                    type: 'date',
                },
            },
            BillingStart: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.BillingStart,
                group: 'Subscription',
                required: false,
                input: {
                    label: 'Billing Start',
                    type: 'date',
                },
            },
            BillingEnd: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.BillingEnd,
                group: 'Subscription',
                required: false,
                input: {
                    label: 'Billing End',
                    type: 'date',
                },
            },
            Status: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.Status,
                group: 'Subscription',
                required: true,
                select: {
                    label: 'Status',
                    options: subscriptionStatusEditOptions,
                    readonly: true,
                },
            },
            DefaultPaymentMethod: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.DefaultPaymentMethod,
                group: 'Billing',
                required: true,
                select: {
                    label: 'Default Payment Method',
                    options: [
                        { value: '1', label: 'Credit card' },
                        { value: '2', label: 'Invoice' },
                        { value: '3', label: 'Paper invoice' },
                        { value: '4', label: 'E-invoice' },
                    ],
                    readonly: true,
                },
            },
            RenewalPeriod: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.RenewalPeriod,
                group: 'Billing',
                required: true,
                select: {
                    label: 'Renewal Period',
                    options: [
                        { value: '1', label: 'Monthly' },
                        { value: '3', label: 'Quarterly' },
                        { value: '12', label: 'Yearly' },
                    ],
                    readonly: true,
                },
            },
            InvoicedUntil: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.InvoicedUntil,
                group: 'Billing',
                required: false,
                input: {
                    label: 'Invoiced Until',
                    type: 'date',
                    disabled: false,
                },
            },
            PaidUntil: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.PaidUntil,
                group: 'Billing',
                required: false,
                input: {
                    label: 'Paid Until',
                    type: 'date',
                },
            },
            MonthlyDiscount: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.MonthlyDiscount,
                group: 'Billing',
                required: false,
                input: {
                    label: 'Flat Monthly Discount',
                    type: 'number',
                },
            },
            MonthlyDiscountPercentage: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.MonthlyDiscountPercentage,
                group: 'Billing',
                required: false,
                input: {
                    label: 'Monthly Discount Percentage',
                    type: 'number',
                },
            },
            PerAccountDiscount: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.PerAccountDiscount,
                group: 'Billing',
                required: false,
                input: {
                    label: 'Per Account Discount',
                    type: 'number',
                },
            },
            PerAccountCreditLimit: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.PerAccountCreditLimit,
                group: 'Billing',
                required: false,
                input: {
                    label: 'Per Account Credit Limit',
                    type: 'number',
                },
            },
            EmailLimit: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.EmailLimit,
                group: 'Limits',
                required: false,
                input: {
                    label: 'Email Limit',
                    type: 'number',
                },
            },
            ContactLimit: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.ContactLimit,
                group: 'Limits',
                required: false,
                input: {
                    label: 'Contact Limit',
                    type: 'number',
                },
            },
            EmailLimitMonths: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.EmailLimitMonths,
                group: 'Limits',
                required: false,
                select: {
                    label: 'Email Limit Months',
                    options: [
                        { value: '1', label: '1' },
                        { value: '3', label: '3' },
                        { value: '6', label: '6' },
                        { value: '12', label: '12' },
                    ],
                },
            },
            ContactLimitMonths: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.ContactLimitMonths,
                group: 'Limits',
                required: false,
                select: {
                    label: 'Contact Limit Months',
                    options: [
                        { value: '1', label: '1' },
                        { value: '3', label: '3' },
                        { value: '6', label: '6' },
                        { value: '12', label: '12' },
                    ],
                },
            },
            ContactCountMode: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.ContactCountMode,
                group: 'Limits',
                required: false,
                select: {
                    label: 'Contact Count Mode',
                    options: [
                        { value: '1', label: 'Count with distinct ContactIds' },
                        { value: '2', label: 'Count with distinct contact emails' },
                    ],
                },
            },
            UserLimit: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.UserLimit,
                group: 'Limits',
                required: true,
                input: {
                    label: 'User Limit',
                    type: 'number',
                },
            },
            MaxRate: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.MaxRate,
                group: 'Limits',
                required: false,
                input: {
                    label: 'Max Rate',
                    type: 'number',
                },
            },
            ApiToken: {
                modelValue: subscriptionAccess.state.boServiceSubscription!.ApiToken,
                group: 'Limits',
                required: false,
                input: {
                    label: 'API Token',
                    type: 'number',
                },
            },
            Comments: {
                group: 'Other',
                modelValue: subscriptionAccess.state.boServiceSubscription!.Comments,
                textArea: {
                    label: 'Comments',
                },
            },
        };

        state.subscriptionFormReady = true;
    }

    async function saveSubscription() {
        if (!state.subscriptionFormReady) return;

        state.subscriptionForm.setInitData = false;

        let subscription = subscriptionAccess.state.boServiceSubscription!;
        const subscriptionData = state.subscriptionForm.data;

        subscription.Status = subscriptionData.Status.modelValue;
        subscription.Start = subscriptionData.Start.modelValue;
        subscription.End = subscriptionData.End.modelValue;
        subscription.BillingStart = subscriptionData.BillingStart.modelValue;
        subscription.BillingEnd = subscriptionData.BillingEnd.modelValue;
        subscription.DefaultPaymentMethod = subscriptionData.DefaultPaymentMethod.modelValue;
        subscription.RenewalPeriod = subscriptionData.RenewalPeriod.modelValue;
        subscription.PaidUntil = subscriptionData.PaidUntil.modelValue;
        subscription.InvoicedUntil = subscriptionData.InvoicedUntil.modelValue;
        subscription.MonthlyDiscount = subscriptionData.MonthlyDiscount.modelValue;
        subscription.MonthlyDiscountPercentage = subscriptionData.MonthlyDiscountPercentage.modelValue;
        subscription.PerAccountDiscount = subscriptionData.PerAccountDiscount.modelValue;
        subscription.PerAccountCreditLimit = subscriptionData.PerAccountCreditLimit.modelValue;
        subscription.EmailLimit = subscriptionData.EmailLimit.modelValue;
        subscription.ContactLimit = subscriptionData.ContactLimit.modelValue;
        subscription.EmailLimitMonths = subscriptionData.EmailLimitMonths.modelValue;
        subscription.ContactLimitMonths = subscriptionData.ContactLimitMonths.modelValue;
        subscription.ContactCountMode = subscriptionData.ContactCountMode.modelValue;
        subscription.UserLimit = subscriptionData.UserLimit.modelValue;
        subscription.MaxRate = subscriptionData.MaxRate.modelValue;
        subscription.ApiToken = subscriptionData.ApiToken.modelValue;
        subscription.Comments = subscriptionData.Comments.modelValue;

        const success = await subscriptionAccess.saveSubscription(subscription);
        if (success) {
            state.subscriptionForm.setInitData = true;

            if (subscription.Status === ServiceSubscriptionStatus.Cancelled.toString()) {
                state.showCancellationReasons = true;
            }
        }
    }

    async function openChangeSubscriptionModal() {
        state.showChangeSubscriptionModal = true;
    }

    function closeChangeSubscriptionModal() {
        state.showChangeSubscriptionModal = false;
    }

    async function serviceSubscriptionServiceLevelUpdated() {
        closeChangeSubscriptionModal();

        state.subscriptionFormReady = false;
        await Promise.all([
            subscriptionAccess.getServiceSubscription(props.serviceSubscriptionId, false).then(() => {
                setupSubscriptionFormData();
                state.subscriptionFormReady = true;
            }),
            subscriptionAddonsAccess.refreshTierFeatures(),
        ]);
    }

    function closeCancellationReasonsModal() {
        state.showCancellationReasons = false;
    }

    function getCancellationDeadline(paidUntil: Date): string {
        const d = new Date(paidUntil);
        d.setMonth(d.getMonth() - 3);
        return formatDate(d);
    }
</script>

<template>
    <div v-if="subscriptionAccess.state.boServiceSubscription">
        <Loading :show="subscriptionAccess.state.loadingUsageData" loadingStyle="progress-bar" :fixed="true" />

        <div v-if="!subscriptionAccess.state.loadingUsageData">
            <div class="ug-bars m-lg-b" v-if="subscriptionAccess.state.usageData">
                <div class="row">
                    <div class="col-md-6">
                        <p class="fw-600 m-xs-b">
                            {{ $Resources.ClientCompanyEmailCountLabel }}
                            <span v-if="subscriptionAccess.state.boServiceSubscription.EmailLimitMonths == 1">
                                {{ $Resources.ClientCompanyEmailCountMonthLabel }}
                            </span>
                            <span v-else>
                                <span>{{ subscriptionAccess.state.boServiceSubscription.EmailLimitMonths }}</span>
                                {{ $Resources.ClientCompanyEmailCountMonthsLabel }}
                            </span>
                        </p>
                        <p class="h2 m-0-t">
                            <span>{{ formatNumber(subscriptionAccess.state.usageData.EmailCount) }}</span>
                            <small>
                                /
                                <span>{{ formatNumber(subscriptionAccess.state.boServiceSubscription.EmailLimit) }}</span>
                            </small>
                        </p>
                        <div class="bar" v-if="subscriptionAccess.state.usageData.EmailCount <= subscriptionAccess.state.boServiceSubscription.EmailLimit">
                            <span
                                :style="{
                                    width:
                                        (subscriptionAccess.state.usageData.EmailCount / subscriptionAccess.state.boServiceSubscription.EmailLimit) * 100 + '%',
                                }"
                                class="bar-value received pull-left"></span>
                        </div>
                        <div class="bar" v-else>
                            <span class="bar-value bg-unsubscribed pull-left" style="width: 100%"></span>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <p class="fw-600 m-xs-b">
                            {{ $Resources.ClientCompanyContactCountLabel }}
                            <span v-if="subscriptionAccess.state.boServiceSubscription.ContactLimitMonths == 1">
                                {{ $Resources.ClientCompanyEmailCountMonthLabel }}
                            </span>
                            <span v-else>
                                <span>{{ subscriptionAccess.state.boServiceSubscription.ContactLimitMonths }}</span>
                                {{ $Resources.ClientCompanyEmailCountMonthsLabel }}
                            </span>
                        </p>
                        <p class="h2 m-0-t">
                            <span>{{ formatNumber(subscriptionAccess.state.usageData.ContactCount) }}</span>
                            <small>
                                /
                                <span>{{ formatNumber(subscriptionAccess.state.boServiceSubscription.ContactLimit) }}</span>
                            </small>
                        </p>
                        <div class="bar" v-if="subscriptionAccess.state.usageData.ContactCount <= subscriptionAccess.state.boServiceSubscription.ContactLimit">
                            <span
                                :style="{
                                    width:
                                        (subscriptionAccess.state.usageData.ContactCount / subscriptionAccess.state.boServiceSubscription.ContactLimit) * 100 +
                                        '%',
                                }"
                                class="bar-value received pull-left"></span>
                        </div>
                        <div class="bar" v-else>
                            <span class="bar-value bg-unsubscribed pull-left" style="width: 100%"></span>
                        </div>
                    </div>
                </div>
            </div>

            <h3 class="h4">{{ $Resources.AccountChartIssueCountHeading }}</h3>

            <div class="chart-container">
                <Loading :show="subscriptionAccess.state.loadingUsageData" loadingStyle="progress-bar" :fixed="true" />

                <Chart
                    v-if="subscriptionAccess.state.usageData && state.chartData && state.chartOptions"
                    type="bar"
                    :data="state.chartData"
                    :options="state.chartOptions" />
            </div>
        </div>

        <div class="row m-lg-t">
            <div class="col-md-6">
                <p class="m-lg-b">
                    {{ $Resources.ContractStartLabel }}:
                    <strong class="d-block m-xs-t">{{ formatDate(subscriptionAccess.state.boServiceSubscription.Start) }}</strong>
                </p>
            </div>
            <div class="col-md-6">
                <p class="m-lg-b">
                    {{ $Resources.NextRenewalDateLabel }}:
                    <strong class="d-block m-xs-t" v-if="subscriptionAccess.state.boServiceSubscription.PaidUntil">
                        {{ formatDate(subscriptionAccess.state.boServiceSubscription.PaidUntil) }}
                    </strong>
                    <strong class="d-block m-xs-t" v-else>{{ $Resources.ValueIsMissing }}</strong>
                </p>
            </div>
        </div>

        <BsAlert :type="AlertTypes.Info" :dismissible="false" v-if="subscriptionAccess.state.boServiceSubscription.PaidUntil">
            <template #content>
                {{ $Resources.ContractCancellationDeadlineInfoPart1 }}
                <strong>{{ getCancellationDeadline(subscriptionAccess.state.boServiceSubscription.PaidUntil) }}</strong>
                {{ $Resources.ContractCancellationDeadlineInfoPart2 }}
            </template>
        </BsAlert>
    </div>

    <div class="form-group">
        <button type="button" class="btn btn-default" @click="openChangeSubscriptionModal">Change subscription</button>
    </div>

    <BsAlert
        :type="AlertTypes.Info"
        :dismissible="false"
        v-if="subscriptionAccess.state.openAIConsumption != null"
        :title="formatNumber(subscriptionAccess.state.openAIConsumption, 1) + '%'"
        content="of this subscription's monthly quota for the AI assistant has been used this month."></BsAlert>

    <Form
        v-if="state.subscriptionFormReady"
        class="fieldset-grid"
        :data="state.subscriptionForm.data"
        :submitting="subscriptionAccess.state.savingSubscription"
        :buttonSticky="true"
        :setInitData="state.subscriptionForm.setInitData"
        @update:isValid="(key, newVal) => (state.subscriptionForm!.data[key].isValid = newVal)"
        @update:modelValue="(key, newVal) => (state.subscriptionForm.data[key].modelValue = newVal)"
        @onSubmit="saveSubscription"></Form>

    <ChangeSubscriptionModal
        :service-subscription-id="props.serviceSubscriptionId"
        :show="state.showChangeSubscriptionModal"
        @close="closeChangeSubscriptionModal"
        @serviceSubscriptionServiceLevelUpdated="serviceSubscriptionServiceLevelUpdated" />

    <CancellationReasonsModal :show="state.showCancellationReasons" @close="closeCancellationReasonsModal" />
</template>

<style scoped>
    .chart-container {
        position: relative;
        height: 200px;
    }
</style>
